import React, { cloneElement, useCallback } from 'react';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core';
import { Dialog } from 'components/common';
import SearchInput from 'components/list/SearchInput';
import { Filters } from 'icons';
import { debounce, set } from 'lodash';
import { Field, Form } from 'react-final-form';

const useStyles = makeStyles((theme) => ({
  button: {
    color: 'white',
    '& button': {
      color: 'inherit',
    },
  },
  form: {
    width: '100%',
    paddingRight: theme.spacing(),
    '& .hide-filter, & .MuiInput-input, & .MuiFormLabel-root, & .MuiSelect-icon': {
      color: 'white',
    },
    '& .MuiSelect-select': {
      minWidth: 50,
    },
    '& .MuiInput-underline:before': {
      borderBottomColor: 'white',
    },
    '& .MuiChip-label': {
      color: theme.palette.primary.main,
    },
    '& .MuiChip-avatar': {
      width: 24,
      height: 24,
    },
  },
  dialogRoot: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

const DialogTitle = ({ onFilterReset }) => (
  <>
    Filters
    <Button color="primary" onClick={onFilterReset}>
      Reset
    </Button>
  </>
);

const FilterButton = (props) => (
  <Button variant="contained" {...props}>
    <Filters />
  </Button>
);

const Filter = (props) => {
  const {
    resource,
    children,
    filterValues,
    initialFilters,
    setFilters,
    basePath,
    btnProps = {},
    classes: classesOverride = {},
    withSearch,
    enableLiveSearch = true,
    filterDefaultValues = {},
    loading,
    searchKey = 'q',
    showChips = true,
  } = props;

  const classes = { ...useStyles(), ...classesOverride };
  const [open, setOpen] = React.useState(false);
  const handleClickOpen = useCallback(() => setOpen(true), [setOpen]);
  const handleSubmitClose = useCallback(() => setOpen(false), [setOpen]);
  const filters = React.Children.toArray(children);

  const handleClose = (form) => () => {
    setOpen(false);
    form.reset();
  };

  const mergedFilterValues = { ...filterDefaultValues, ...filterValues };
  const mergedInitialValuesWithDefaultValues = mergeInitialValuesWithDefaultValues({
    initialValues: mergedFilterValues,
    filters,
  });

  const buttonProps = { onClick: handleClickOpen, ...btnProps };

  const onFilterReset = (form) => {
    form.reset();
    setFilters(initialFilters || filterDefaultValues);
  };

  const debouncedSubmit = debounce(function (value, submitCallback) {
    submitCallback(value);
  }, 150);

  const onLiveSearch = (e, submitCallback) => {
    debouncedSubmit({ [e.target.name]: e.target.value }, submitCallback);
  };

  return (
    <Form
      onSubmit={(values) => {
        setFilters(values);
        handleSubmitClose();
      }}
      initialValues={mergedInitialValuesWithDefaultValues}
      render={({ handleSubmit, form }) => {
        return (
          <form onSubmit={handleSubmit} className={classes.form}>
            {withSearch && (
              <Field
                key={searchKey}
                name={searchKey}
                label="Search"
                filterButton={<FilterButton {...buttonProps} />}
                render={(inputProps) => (
                  <SearchInput
                    {...inputProps}
                    {...{
                      filterValues: mergedFilterValues,
                      filters,
                      setFilters,
                      resource,
                      showChips,
                    }}
                    loading={loading}
                    onSearchKeyUp={(e) => (enableLiveSearch ? onLiveSearch(e, handleSubmit) : null)}
                  />
                )}
              />
            )}
            {!withSearch && <FilterButton {...buttonProps} />}
            <Dialog
              open={open}
              title={<DialogTitle onFilterReset={() => onFilterReset(form)} />}
              confirmText="Apply"
              onCancel={handleClose(form)}
              onConfirm={handleSubmit}>
              <Box className={classes.dialogRoot}>
                {filters.map((filter) => (
                  <Field
                    key={filter.props.source}
                    name={filter.props.source}
                    label={filter.props.label}
                    render={(inputProps) =>
                      cloneElement(filter, {
                        ...inputProps,
                        basePath,
                        resource,
                        record: {},
                      })
                    }
                  />
                ))}
              </Box>
            </Dialog>
          </form>
        );
      }}
    />
  );
};

const mergeInitialValuesWithDefaultValues = ({ initialValues, filters }) => ({
  ...filters
    .filter((filterElement) => filterElement.props.alwaysOn && filterElement.props.defaultValue)
    .reduce(
      (acc, filterElement) =>
        set({ ...acc }, filterElement.props.source, filterElement.props.defaultValue),
      {}
    ),
  ...initialValues,
});

const getFilterIfNoButton = (props) => (props.context === 'button' ? null : <Filter {...props} />);

export default getFilterIfNoButton;
